import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Optional } from '@angular/core';
import { environment } from '../../environments/environment';
import { PROJECT_NAME, ProjectName, ProjectNames } from '../tokens';

/**
 * Service for Google Tag Manager container loading
 * Documentation: https://developers.google.com/tag-platform/tag-manager/web
 *
 * The container id comes from the environment, so non-prod builds load a
 * deliberately wrong id and never pollute production data.
 *
 * In production the container is served by our own tagging server rather than
 * googletagmanager.com — see taggingOrigin().
 */
@Injectable({
  providedIn: 'root',
})
export class GtmService {
  private readonly containerId = environment.gtmContainerId;
  private readonly dataLayerName = 'dataLayer';
  private isInitialized = false;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Optional() @Inject(PROJECT_NAME) private projectName?: ProjectName
  ) {}

  init(): void {
    if (this.isInitialized || !this.isEnabledProject() || typeof window === 'undefined') {
      return;
    }

    this.isInitialized = true;

    const targetWindow = window as Window &
      typeof globalThis & {
        dataLayer?: unknown[];
      };

    const taggingOrigin = this.taggingOrigin();

    targetWindow.dataLayer = targetWindow.dataLayer || [];

    // Read by a GTM Data Layer Variable that feeds server_container_url on the Google
    // tags. It has to come from here rather than be a constant in the container: the
    // origin differs per apex, and it must be in the dataLayer before the container loads.
    if (taggingOrigin) {
      targetWindow.dataLayer.push({ server_container_url: taggingOrigin });
    }

    targetWindow.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

    // The randomized loader path replaces gtm.js, and the tagging server rejects it
    // without the trailing slash.
    const scriptUrl = taggingOrigin
      ? `${taggingOrigin}/${environment.gtmLoaderPath}/?id=${this.containerId}`
      : `https://www.googletagmanager.com/gtm.js?id=${this.containerId}`;

    if (this.document.querySelector(`script[src="${scriptUrl}"]`)) {
      return;
    }

    const script = this.document.createElement('script');
    script.async = true;
    script.src = scriptUrl;

    const firstScript = this.document.getElementsByTagName('script')[0];
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      this.document.head.appendChild(script);
    }
  }

  /**
   * Origin of the tagging server proxying our container: `tm.<apex>` of whichever domain
   * the funnel is served from, so both the container script and the hits the tags send
   * stay first-party. Null on hosts we run no tagging server for (localhost, Firebase
   * preview domains) — those fall back to Google's CDN.
   */
  private taggingOrigin(): string | null {
    const apex = window.location.hostname.split('.').slice(-2).join('.');

    if (!environment.gtmLoaderPath || !environment.gtmTaggingApexes.includes(apex)) {
      return null;
    }

    return `https://tm.${apex}`;
  }

  private isEnabledProject(): boolean {
    const projectName = this.projectName ?? ProjectNames.FUNNEL;

    return (
      projectName === ProjectNames.FUNNEL ||
      projectName === ProjectNames.GOOGLE_FUNNEL ||
      projectName === ProjectNames.SHORT_FUNNEL ||
      projectName === ProjectNames.KINDLE_FUNNEL ||
      projectName === ProjectNames.ROMANCE_FUNNEL ||
      projectName === ProjectNames.READER_FUNNEL
    );
  }
}
